import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

// local imports
import loadingCalendar from 'assets/icons/loading-calendar.png'
import successImage from 'assets/images/success-image.svg'
import {
  CoiffeurGender,
  ICoiffeurSaloonSpecsFilteredList,
  ICoiffeurSearchForm,
  IFilteredCoiffeurList,
  IFilteredCoiffeurSalonList,
} from 'types/CoiffeurType'
import CoiffeurService from 'service/CoiffeurService'
import useCoiffeurReservationModal from '../hooks/use-reservation-modal'
import FormModal from 'components/ui/modal/FormModal'
import Loader from 'components/ui/Loader'
import NotificationDialog from 'components/ui/NotificationDialog'
import CoiffeurSearchForm from './CoiffeurSearchForm'
import ReservationModalWrapper from '../reservation-scheduler/ReservationModal'
import { SearchModalHeader } from './styled'

interface ICoiffeurSearchModalProps {
  filteredDate: Date
  formModal: boolean
  setFormModal: Dispatch<SetStateAction<boolean>>
  salon: IFilteredCoiffeurSalonList
  setShowModal: Dispatch<SetStateAction<boolean>>
  cancelClose?: () => void
}

export default function CoiffeurSearchModal({
  filteredDate,
  formModal,
  setFormModal,
  salon,
  setShowModal,
  cancelClose,
}: ICoiffeurSearchModalProps) {
  const { t } = useTranslation()
  const history = useHistory()
  const [loading, setLoading] = useState(false)
  const [saloonSpecs, setSaloonSpecs] = useState<ICoiffeurSaloonSpecsFilteredList[] | []>([])
  const [coiffeurList, setCoiffeurList] = useState<IFilteredCoiffeurList[] | []>([])

  /* ######### Coiffeur Saloon Data Destructuring ######### */
  const {
    id,
    name,
    gender,
    listMainServiceStr,
    floor: { locationName, locationId, districtName, cityName, countryName, blockName },
  } = salon || {}

  /* Get Coiffeur Saloon Specs (Gender related) */
  useEffect(() => {
    setLoading(true)
    CoiffeurService.getCoiffeurSaloonSpecs({
      coiffeurSaloonId: id,
      isOnlyUserGender: true,
    })
      .then((res) => setSaloonSpecs(res.data))
      .catch((error) => console.log(error))
      .finally(() => setLoading(false))
  }, [id])

  /* Location String */
  const locationStr = `${locationName}, ${districtName}, ${cityName}, ${countryName}, ${blockName}`
  const coiffeurServices = listMainServiceStr.join(', ')
  /* Form  */
  /* Filter form default values */
  const defaultValues: ICoiffeurSearchForm = {
    mainServiceId: 0,
    subServiceId: 0,
    coiffeur: null,
    reservationDate: filteredDate,
  }

  /* Search form state  */
  const { watch, resetField, control } = useForm<ICoiffeurSearchForm>({
    defaultValues,
  })
  const mainServiceId = watch('mainServiceId')
  const subServiceId = watch('subServiceId')
  const reservationDate = watch('reservationDate')
  const _selectedCoiffeur = watch('coiffeur')

  /* Get Coiffeurs - Coiffeur Workers List */
  useEffect(() => {
    if (!!id && !!subServiceId) {
      setLoading(true)
      CoiffeurService.getFilteredCoiffeurList({
        coiffeurSalloonId: id,
        coiffeurMainServiceId: mainServiceId,
        coiffeurSubServiceId: subServiceId,
        isIncludeNotActives: false,
      })
        .then((r) => setCoiffeurList(r.data))
        .catch((err) => console.log(err))
        .finally(() => setLoading(false))
    }
  }, [id, mainServiceId, subServiceId])

  /* ################## Reservation Modal ##################### */

  /* Reservation Modal (Open - Close Modal, Selected Coiffeur). | Custom Hook */
  const {
    openReservationModal,
    closeReservationModal,
    reservationModal,
    selectedCoiffeur,
    setSelectedCoiffeur,
    notification,
    setNotification,
  } = useCoiffeurReservationModal()

  /* Modal Togglers */
  const saveAndCloseFormModal = () => {
    if (!!_selectedCoiffeur) {
      openReservationModal(_selectedCoiffeur)
      setFormModal(false)
    }
  }

  const cancelAndClose = () => {
    cancelClose?.()
    setShowModal(false)
    setFormModal(false)
  }

  const resetAfterSave = () => {
    cancelClose?.()
    setNotification(false)
    setShowModal(false)
    setFormModal(false)
  }

  return (
    <>
      <FormModal
        closable
        focusOnShow={false}
        header={<span>{t('dialog-texts.coiffeur-reservation-header')} (1/2)</span>}
        disabled={!_selectedCoiffeur}
        onSave={saveAndCloseFormModal}
        visible={!!formModal}
        onHide={cancelAndClose}
        onCancel={cancelAndClose}>
        <SearchModalHeader>
          <div className="dialog-subheader">
            <span className="name">
              {name} ({t(`most-used-words.${CoiffeurGender[gender]}`)})
            </span>
            <span className="location">{locationStr}</span>
            <span className="services">{coiffeurServices}</span>
          </div>
        </SearchModalHeader>

        <CoiffeurSearchForm
          watch={watch}
          coiffeurList={coiffeurList}
          salonSpecs={saloonSpecs}
          control={control}
          resetField={resetField}
        />

        {loading ? (
          <div className="my-3 w-100">
            <Loader src={loadingCalendar} />
          </div>
        ) : null}
      </FormModal>

      {/* Reservation Timetable Modal */}
      {selectedCoiffeur ? (
        <ReservationModalWrapper
          salon={salon}
          filterDate={reservationDate}
          subServiceId={subServiceId}
          locationId={locationId}
          coiffeurs={coiffeurList}
          open={reservationModal}
          openReservationModal={openReservationModal}
          closeReservationModal={closeReservationModal}
          openFormModal={() => setFormModal(true)}
          showNotification={() => setNotification(true)}
          setSelectedCoiffeur={setSelectedCoiffeur}
          selectedCoiffeur={selectedCoiffeur}
        />
      ) : null}

      <NotificationDialog
        display={notification}
        imgSrc={successImage}
        goHomeButton={true}
        title={t('coiffeur-res-modal.notification-title')}
        content={t('coiffeur-res-modal.notification-text')}
        firstButtonLabel={t('buttons.go-to-calendar')}
        onHide={resetAfterSave}
        onSubmit={() => history.push('/calendar')}
        selectedType={null}
        setSelectedType={undefined}
        types={[]}
      />
    </>
  )
}
